import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import useModalA11y from '../../hooks/useModalA11y';
import HostAgreementContent from './HostAgreementContent';
import LegalDocumentActions from '../LegalDocumentActions/LegalDocumentActions';
import './LeaseAgreementGate.css';

function buildCopy(listings) {
    const isBatch = listings.length > 1;
    const single = listings.length === 1 ? listings[0] : null;

    return {
        title: isBatch
            ? `Property Lease Agreement — ${listings.length} properties`
            : 'Property Lease Agreement',
        subtitle: isBatch
            ? `Standard ParkPro lease covering all ${listings.length} properties in this batch. Each property is bound by the same terms.`
            : `Standard ParkPro lease for ${single?.name || 'this property'}. Defines the right-to-use terms for ParkPro to broker parking on this lot.`,
        checkboxLabel: isBatch
            ? `I have read and accept the lease agreement for all ${listings.length} properties listed below.`
            : `I have read and accept the lease agreement for ${single?.name || 'this property'}.`,
    };
}

export default function LeaseAgreementGate({
    open = false,
    listings = [],
    leaseVersion,
    busy = false,
    host = {},
    onAccept,
    onCancel,
}) {
    const [checked, setChecked] = useState(false);
    const contentRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const [signerName, setSignerName] = useState('');
    // A host must scroll far enough to actually see Section 12 before the
    // acceptance controls even appear — no accepting a document you never
    // scrolled through.
    const [reachedSignatures, setReachedSignatures] = useState(false);
    const canAccept = reachedSignatures && signerName.trim().length > 0 && checked;

    const handleCancel = useCallback(() => {
        setChecked(false);
        setSignerName('');
        setReachedSignatures(false);
        onCancel?.();
    }, [onCancel]);

    const modalRef = useModalA11y({ open, onClose: handleCancel });

    // Resets the acceptance checkbox any time the signature text changes —
    // without this, a host could check "I have read and accept..." then
    // edit the still-live signature input to a different name afterward,
    // submitting the new name without re-affirming the checkbox against it.
    const handleSignerNameChange = useCallback((value) => {
        setSignerName(value);
        setChecked(false);
    }, []);

    // Fires once the Signatures section (#lease-signature-section, rendered by
    // HostAgreementContent) scrolls into view within the modal's scrollable
    // content area — never resets once true, so scrolling back up to re-read
    // an earlier clause doesn't re-lock the host out.
    useEffect(() => {
        if (!open) return undefined;
        const root = scrollContainerRef.current;
        const target = contentRef.current?.querySelector('#lease-signature-section');
        if (!root || !target) return undefined;

        // Only trust an intersection once the host has actually scrolled,
        // UNLESS there's nothing to scroll at all (content fits the
        // container) — in that case immediate visibility is legitimate, not
        // a bypass. Without this, IntersectionObserver's synchronous first
        // callback (threshold: 0 reports current state as soon as observe()
        // runs) could report the target as already visible under an
        // unusually tall/zoomed-out container, satisfying "must scroll to
        // accept" with zero actual scrolling.
        let hasScrolled = root.scrollHeight <= root.clientHeight;

        const handleScroll = () => { hasScrolled = true; };
        root.addEventListener('scroll', handleScroll, { passive: true });

        const observer = new IntersectionObserver((entries) => {
            if (hasScrolled && entries.some((entry) => entry.isIntersecting)) {
                setReachedSignatures(true);
                observer.disconnect();
                root.removeEventListener('scroll', handleScroll);
            }
        }, { root, threshold: 0 });

        observer.observe(target);
        return () => {
            observer.disconnect();
            root.removeEventListener('scroll', handleScroll);
        };
    }, [open]);

    // Resets local state when the modal actually closes (for any reason),
    // rather than inside handleAccept itself. useLeaseGate's own
    // handleAccept never rejects (its try/catch always resolves, calling
    // closeGate() only on success), so `open` transitioning to false is the
    // only reliable success signal available here. On a FAILED submit
    // `open` stays true and this effect does not fire, so
    // reachedSignatures/signerName/checked survive exactly as the host left
    // them — the IntersectionObserver above has already disconnected after
    // its one-shot fire and can't re-arm without a full close/reopen, so
    // resetting on failure would otherwise permanently strand the host on
    // "please scroll to accept".
    const wasOpenRef = useRef(open);
    useEffect(() => {
        if (wasOpenRef.current && !open) {
            setChecked(false);
            setSignerName('');
            setReachedSignatures(false);
        }
        wasOpenRef.current = open;
    }, [open]);

    const handleAccept = useCallback(async () => {
        if (!canAccept || busy) return;
        await onAccept?.(signerName.trim());
    }, [canAccept, busy, onAccept, signerName]);

    if (!open || !listings.length) return null;

    const copy = buildCopy(listings);

    return createPortal(
        <div className="lease-agreement-gate-root">
            <div
                className="legal-gate-overlay open"
                role="presentation"
                onClick={(e) => {
                    if (e.target === e.currentTarget) handleCancel();
                }}
            >
                <div
                    ref={modalRef}
                    className="legal-gate-modal"
                    role="dialog"
                    aria-labelledby="lease-gate-title"
                    aria-modal="true"
                    tabIndex={-1}
                >
                    <div className="legal-gate-head">
                        <button
                            type="button"
                            className="legal-gate-close"
                            aria-label="Close"
                            onClick={handleCancel}
                            disabled={busy}
                        >
                            ×
                        </button>
                        <div className="legal-gate-title" id="lease-gate-title">
                            {copy.title}
                        </div>
                        <p className="legal-gate-subtitle">{copy.subtitle}</p>
                        <LegalDocumentActions targetRef={contentRef} title={copy.title} />
                    </div>
                    <div className="legal-gate-content" ref={scrollContainerRef}>
                        <div ref={contentRef}>
                            <HostAgreementContent
                                listings={listings}
                                host={host}
                                signerName={signerName}
                                onSignerNameChange={handleSignerNameChange}
                            />
                        </div>
                    </div>
                    <div className="legal-gate-foot">
                        {!reachedSignatures ? (
                            <p className="legal-gate-hint">Please scroll to accept.</p>
                        ) : !signerName.trim() ? (
                            <p className="legal-gate-hint">Please sign before accepting.</p>
                        ) : (
                            <label className="legal-gate-check">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={busy}
                                    onChange={(e) => setChecked(e.target.checked)}
                                />
                                <span>{copy.checkboxLabel}</span>
                            </label>
                        )}
                        <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={busy}
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={!canAccept || busy}
                            onClick={handleAccept}
                        >
                            {busy ? 'Submitting…' : 'Accept & submit'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

LeaseAgreementGate.propTypes = {
    open: PropTypes.bool,
    listings: PropTypes.arrayOf(PropTypes.shape({
        _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string,
        addr: PropTypes.string,
    })),
    leaseVersion: PropTypes.string,
    busy: PropTypes.bool,
    host: PropTypes.shape({
        entityName: PropTypes.string,
        contactName: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        address: PropTypes.string,
        effectiveDate: PropTypes.string,
    }),
    onAccept: PropTypes.func,
    onCancel: PropTypes.func,
};
